/**
 * Generic error page component — aurora theme.
 *
 * Handles all error routes: 401, 403, 404, 500, 503.
 * Mirrors the legacy AngularJS error page templates.
 */
import { useParams, Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLock,
  faBan,
  faMagnifyingGlassMinus,
  faTriangleExclamation,
  faCloudSlash,
  faHouse,
  faRightToBracket,
} from '@fortawesome/pro-solid-svg-icons';
import AuroraBackground from '@/components/layout/AuroraBackground';

interface ErrorConfig {
  title: string;
  message: string;
  icon: IconDefinition;
}

const ERROR_CONFIG: Record<string, ErrorConfig> = {
  '401': { title: 'Unauthorized',          message: 'You need to log in to access this page.',                     icon: faLock },
  '403': { title: 'Forbidden',             message: 'You do not have permission to access this resource.',         icon: faBan },
  '404': { title: 'Not Found',             message: 'The page you are looking for does not exist.',                icon: faMagnifyingGlassMinus },
  '500': { title: 'Internal Server Error', message: 'Something went wrong on our end. Please try again later.',    icon: faTriangleExclamation },
  '503': { title: 'Service Unavailable',   message: 'The service is temporarily unavailable. Please try again later.', icon: faCloudSlash },
};

export default function ErrorPage({ code }: Readonly<{ code?: string }>) {
  const params = useParams();
  const errorCode = code || params.code || '404';
  const config = ERROR_CONFIG[errorCode] || ERROR_CONFIG['404'];

  return (
    <>
      <AuroraBackground />
      <div className="min-h-[var(--app-content-height)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="aurora-panel px-8 py-10 text-center">
            <div className="inline-flex items-center justify-center size-20 rounded-full mb-6"
              style={{ background: 'rgb(var(--brew-blue-light) / 0.04)', border: '1px solid rgb(var(--brew-blue-light) / 0.12)' }}>
              <FontAwesomeIcon icon={config.icon} className="text-brew-blue-light text-[32px]" />
            </div>
            <h1 className="text-5xl font-bold tracking-tight mb-2 text-aurora-text" >
              {errorCode}
            </h1>
            <h2 className="text-lg font-semibold mb-3 text-aurora-text" >
              {config.title}
            </h2>
            <p className="text-sm mb-6 text-aurora-text-muted/70" >
              {config.message}
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/dashboard" className="aurora-btn aurora-btn-primary">
                <FontAwesomeIcon icon={faHouse} /> Dashboard
              </Link>
              {errorCode === '401' && (
                <Link to="/login" className="aurora-btn aurora-btn-primary">
                  <FontAwesomeIcon icon={faRightToBracket} /> Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
